/**
 * usePreDepartureConfig.js
 *
 * Hook for managing vessel-specific pre-departure check configuration.
 * REWORKED: Removed toggle concept, added audit trail integration.
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';
import {
  CONFIG_VERSION,
  REQUIRED_CHECKS,
  getAllStandardChecks,
  CHECK_CATEGORIES
} from '../../../config/operations/preDepartureChecksLibrary';

// New config version for migration
const NEW_CONFIG_VERSION = 2;

// RBAC helper
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Migrate from v1 (toggle-based) to v2 (item-based)
 */
const migrateConfig = (oldConfig, allStandardChecks) => {
  if (!oldConfig || oldConfig.version >= NEW_CONFIG_VERSION) {
    return oldConfig;
  }

  // Build items array from:
  // 1. Required checks (always included)
  // 2. All standard library checks (no more "enabled" filter)
  // 3. Existing custom checks
  const items = [];

  // Add required checks
  REQUIRED_CHECKS.forEach(check => {
    items.push({
      ...check,
      source: 'library',
      addedAt: new Date().toISOString(),
      addedBy: { name: 'System', userId: 'system' }
    });
  });

  // Add all standard checks (previously we only added "enabled" ones - now ALL)
  allStandardChecks.forEach(check => {
    if (!items.find(i => i.id === check.id)) {
      items.push({
        ...check,
        required: false,
        source: 'library',
        addedAt: new Date().toISOString(),
        addedBy: { name: 'System', userId: 'system' }
      });
    }
  });

  // Migrate custom checks
  (oldConfig.customChecks || []).forEach(check => {
    items.push({
      ...check,
      required: false,
      source: 'custom'
    });
  });

  return {
    version: NEW_CONFIG_VERSION,
    revision: 1,
    items,
    lastModified: new Date().toISOString(),
    modifiedBy: { name: 'Migration', userId: 'system' }
  };
};

/**
 * Generate default config for new vessels
 */
const getDefaultConfig = (allStandardChecks) => {
  const items = [];

  // Add required checks
  REQUIRED_CHECKS.forEach(check => {
    items.push({
      ...check,
      source: 'library',
      addedAt: new Date().toISOString(),
      addedBy: { name: 'System', userId: 'system' }
    });
  });

  // Add all standard checks
  allStandardChecks.forEach(check => {
    if (!items.find(i => i.id === check.id)) {
      items.push({
        ...check,
        required: false,
        source: 'library',
        addedAt: new Date().toISOString(),
        addedBy: { name: 'System', userId: 'system' }
      });
    }
  });

  return {
    version: NEW_CONFIG_VERSION,
    revision: 1,
    items,
    lastModified: new Date().toISOString(),
    modifiedBy: { name: 'System', userId: 'system' }
  };
};

export const usePreDepartureConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  const allStandardChecks = useMemo(() => getAllStandardChecks(), []);

  const { value: rawConfig, setValue: setConfig } = useStorageState(
    'predeparture_config',
    getDefaultConfig(allStandardChecks)
  );

  // Migrate if needed
  const config = useMemo(() => {
    if (!rawConfig) return getDefaultConfig(allStandardChecks);
    if (rawConfig.version < NEW_CONFIG_VERSION) {
      return migrateConfig(rawConfig, allStandardChecks);
    }
    return rawConfig;
  }, [rawConfig, allStandardChecks]);

  // Auto-save migration
  useEffect(() => {
    if (rawConfig && rawConfig.version < NEW_CONFIG_VERSION) {
      const migrated = migrateConfig(rawConfig, allStandardChecks);
      setConfig(migrated);
    }
  }, [rawConfig, allStandardChecks, setConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get all items for display/use
   */
  const items = useMemo(() => config.items || [], [config]);

  /**
   * Get items by category
   */
  const getItemsByCategory = useCallback((categoryId) => {
    return items.filter(item => item.category === categoryId);
  }, [items]);

  /**
   * Get count summary
   */
  const getCounts = useCallback(() => {
    const required = items.filter(i => i.required || i.regulation).length;
    const custom = items.filter(i => i.source === 'custom' || i.source === 'imported').length;
    const standard = items.length - required - custom;
    return { total: items.length, required, standard, custom };
  }, [items]);

  /**
   * Get enabled count by category (for backward compatibility with ChecklistSettingsTab)
   */
  const getEnabledCountByCategory = useCallback(() => {
    const counts = getCounts();
    return counts;
  }, [getCounts]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Add a new item
   */
  const addItem = useCallback(async (newItem) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[usePreDepartureConfig] Permission denied');
      return null;
    }

    const itemWithMeta = {
      ...newItem,
      id: newItem.id || `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: newItem.source || 'custom',
      addedAt: new Date().toISOString(),
      addedBy: { name: currentUser?.name || 'Unknown', userId: currentUser?.id || 'unknown' }
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      items: [...config.items, itemWithMeta],
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.CREATE,
        'predeparture_config_item',
        { id: itemWithMeta.id, vesselId: currentVessel?.id, item: itemWithMeta },
        { userId: currentUser?.id, name: currentUser?.name }
      );
    } catch (e) {
      console.warn('[usePreDepartureConfig] Audit log failed:', e);
    }

    return itemWithMeta;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Update an existing item
   */
  const updateItem = useCallback(async (itemId, updates) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[usePreDepartureConfig] Permission denied');
      return false;
    }

    const existingItem = config.items.find(i => i.id === itemId);
    if (!existingItem) return false;

    // Cannot edit required items
    if (existingItem.required || existingItem.regulation) {
      console.warn('[usePreDepartureConfig] Cannot edit required items');
      return false;
    }

    const updatedItem = {
      ...existingItem,
      ...updates,
      updatedAt: new Date().toISOString(),
      updatedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      items: config.items.map(i => i.id === itemId ? updatedItem : i),
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.UPDATE,
        'predeparture_config_item',
        { id: itemId, vesselId: currentVessel?.id, item: updatedItem },
        { userId: currentUser?.id, name: currentUser?.name },
        existingItem
      );
    } catch (e) {
      console.warn('[usePreDepartureConfig] Audit log failed:', e);
    }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Remove an item
   */
  const removeItem = useCallback(async (itemId, reason = '') => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[usePreDepartureConfig] Permission denied');
      return false;
    }

    const existingItem = config.items.find(i => i.id === itemId);
    if (!existingItem) return false;

    // Cannot remove required items
    if (existingItem.required || existingItem.regulation) {
      console.warn('[usePreDepartureConfig] Cannot remove required items');
      return false;
    }

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      items: config.items.filter(i => i.id !== itemId),
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.DELETE,
        'predeparture_config_item',
        { id: itemId, vesselId: currentVessel?.id, reason },
        { userId: currentUser?.id, name: currentUser?.name },
        existingItem
      );
    } catch (e) {
      console.warn('[usePreDepartureConfig] Audit log failed:', e);
    }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Data
    config,
    items,
    categories: CHECK_CATEGORIES,

    // Computed
    getItemsByCategory,
    getCounts,
    getEnabledCountByCategory,

    // Mutations (with audit trail)
    addItem,
    updateItem,
    removeItem,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision
  };
};

export default usePreDepartureConfig;
